import { put, take, select, race, call } from "redux-saga/effects";
import { REHYDRATE } from "redux-persist";
import axios from "axios";
import queryString from "query-string-for-all";
import { history } from "../../../browserHistory";
import { load as authenticateUser } from "../auth/actionCreators";
import UserService from "../../../utils/keycloak/UserService";
import {
  LOAD_AUTH_USER_SUCCESS,
  LOAD_AUTH_USER_FAIL,
  LOGOUT_SUCCESS,
  LOGIN_KEYCLOAK
} from "../auth/actionTypes";
import {
  APP_SETUP_COMPLETE,
  APP_SETUP_RESET,
  APP_SETUP_ERROR,
} from "./actionTypes";
import { LOAD_USER_SITES_COMPLETE } from "../userSites/actionTypes";
import {
  updateSelectedSites,
  selectActiveSite,
  updateDaysRange,
} from "../_BETA/selections";
import { TIME_RANGE_OPTIONS } from "../../../components/Analysis/utils/options";

/**
 * Flow for parsing out:
 * ?dateRange=
 * ?sites=
 * (monitoring|insights)/:siteId/
 * Captures requested params from url and appends valid
 * params to app state during app setup
 */
function* handleUrlParamsFlow(): Generator<any, any, any> {
  // If no sites in the url, grab em from the store and append the siteId to the end of URL
  const qs = queryString.parse(history.location.search, {
    parseNumbers: true,
    sort: false,
  });

  // HACK: Have to account for equip view, where siteId is a url parameter
  const equipViewRE = /\/analysis\/(:?insights|monitoring)\/(.+)\/\d+/;
  const urlSites: Set<string> = new Set();
  const siteParam = equipViewRE.exec(window.location.pathname);

  // if daysRange in search, and value is valid, update state
  // otherwise use default
  if (
    "daysRange" in qs &&
    TIME_RANGE_OPTIONS.monitoring.find(
      (daysRangeOption) => daysRangeOption.value === Number(qs.daysRange)
    )
  ) {
    yield put(updateDaysRange(Number(qs.daysRange)));
  } else {
    const defaultDaysRange = yield select(
      (state: Insite.AppState) => state.betaSelections.daysRange
    );
    const parsedDefaultDaysRangeURL = queryString.stringify(
      { ...qs, daysRange: defaultDaysRange },
      { arrayFormat: "comma", sort: false }
    );
    history.replace({
      pathname: history.location.pathname,
      search: parsedDefaultDaysRangeURL,
    });
  }

  // Check for siteId param (equp view url)
  if (siteParam !== null) {
    urlSites.add(siteParam[2]);
  }

  // Check for sites qs parameter
  if ("sites" in qs) {
    const arr = (qs.sites as string).split(",");
    arr.forEach((s) => urlSites.add(s));
  }

  // Validate sites from URL (params + search), and append to selected sites in store
  if (urlSites.size === 0) {
    const { selectedSites } = yield select(
      (state: Insite.AppState) => state.betaSelections
    );
    const parsedDefaultSitesURL = queryString.stringify(
      { ...qs, sites: selectedSites },
      { arrayFormat: "comma", sort: false }
    );
    history.replace({
      pathname: history.location.pathname,
      search: parsedDefaultSitesURL,
    });
    yield put(selectActiveSite(selectedSites[0]));
  } else {
    const { sites } = yield select(
      (state: Insite.AppState) => state.auth.user.portfolio
    );
    const { selectedSites } = yield select(
      (state: Insite.AppState) => state.betaSelections
    );
    const userSelectedSites = new Set([
      ...Array.from(urlSites),
      ...selectedSites,
    ]);
    const siteIdsFromUrl: string[] = Array.from(userSelectedSites);

    // Validate requested sites against user access
    // Filter out invalid sites and log error (if invalid sites present)
    const userSiteIds = Object.keys(sites);
    const validatedSiteIds = siteIdsFromUrl.filter((siteIdFromUrl) =>
      userSiteIds.includes(siteIdFromUrl)
    );
    const userCanAccessSitesFromUrl =
      validatedSiteIds.length === siteIdsFromUrl.length;
    yield put(updateSelectedSites(validatedSiteIds));

    if (siteParam !== null && validatedSiteIds.includes(siteParam[2])) {
      yield put(selectActiveSite(siteParam[2]));
    } else {
      yield put(selectActiveSite(validatedSiteIds[0]));
    }

    try {
      if (!userCanAccessSitesFromUrl) {
        const validatedSearchParams = queryString.stringify(
          { ...qs, sites: validatedSiteIds },
          { arrayFormat: "comma", sort: false }
        );
        history.replace({
          pathname: history.location.pathname,
          search: validatedSearchParams,
        });

        throw new Error("User doesn't have permission");
      }
    } catch (e) {
      console.error(e);
    }
  }
}

export function* appSetupFlow() {
  yield take(REHYDRATE);
  while (true) {

    // LOGIN_KEYCLOAK is now always dispatched from UserService.initKeycloak()
    // once _kc.init() completes, so we always have a live KC token here.
    // The old rehydration fast-path (user.token from persisted Redux state) is
    // removed because it caused a timing race: REHYDRATE fires before _kc.init()
    // resolves, so UserService.getToken() was undefined and the stale persisted
    // token was used, resulting in a 401 → premature logout.
    yield take(LOGIN_KEYCLOAK);
    const { user: loggedInUser } = yield select(
      (state: Insite.AppState) => state.auth
    );
    // Use the live KC token at the moment of the API call
    const loggedInUserWithLiveToken = { ...loggedInUser, token: UserService.getToken() || loggedInUser.token };
    yield put(authenticateUser(loggedInUserWithLiveToken) as any);


    const { authUserSuccess, authUserFailure } = yield race({
      authUserSuccess: take(LOAD_AUTH_USER_SUCCESS),
      authUserFailure: take(LOAD_AUTH_USER_FAIL),
    });

    if (authUserSuccess) {
      const { user: authenticatedUser } = yield select(
        (state: Insite.AppState) => state.auth
      );
      // Append auth header to axios after user validation
      axios.defaults.headers.common[
        "Authorization"
      ] = `Bearer ${authenticatedUser.token}`;

      // Wait for user sites to be loaded
      yield take(LOAD_USER_SITES_COMPLETE);

      // Grab any url sites/date ranges and apply to app state
      yield call(handleUrlParamsFlow);

      yield put({ type: APP_SETUP_COMPLETE });
      yield take(LOGOUT_SUCCESS);
      yield put({ type: APP_SETUP_RESET });
      // Redirect to login after logout
      // NOTE: We're intentionally doing a hard redirect to reset all sagas
      // If another flow has already handled the redirect, just refresh the page.
      if (window.location.pathname === "/login") {
        window.location.reload();
      } else {
        const redirect = `/login`;
        // @ts-ignore
        window.location = redirect;
      }

    }

    if (authUserFailure) {
      yield put({
        type: APP_SETUP_ERROR,
        error: "Failed to authenticate logged in user",
      });
    }
  }
}
